import { useState, useRef } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { withRouter } from 'react-router'
import styled, { css } from 'styled-components'
import Content from '@/components/content'
import ContentWrapper from '@/components/content-wrapper'
import EndTenancyButton from '@/components/end-tenancy-button'
import { WhitePage } from '@/components/page'
import PageTitle from '@/components/page-title'
import config from '@/config'
import useGetRenewalContract from '@/hooks/use-get-renewal-contract'
import useMediaQuery from '@/hooks/use-media-query'
import useRole from '@/hooks/use-role'
import { show as showContractHistoryModal } from '@/pages/Tenants/TenantResources/contract-history-modal'
import { select } from '@/store'
import { breakpoints, colors, fontSizes, fontWeights, structure } from '@/styles'
import PlusIcon from './plus_small.svg?react'
import CloseIcon from './close_dark.svg?react'
import ReviewSignatures from '../review-signatures'
import ReviewDetails from '../review-details'

const Wrapper = styled.div`
  display: flex;
  justify-content: flex-start;
`

const StyledContentWrapper = styled(ContentWrapper)`
  margin: 40px 176px 100px 100px;

  @media screen and (max-width: ${breakpoints.desktop}) {
    max-width: 470px;
    margin: 40px 60px 100px;
  }

  @media screen and (max-width: ${breakpoints.tabletLandscape}) {
    max-width: unset;
    margin: 40px 40px 100px;
  }

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    margin: 40px 0 100px;
  }
`

const ContractOptionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${colors.lightGrey};

  @media screen and (min-width: ${breakpoints.tabletLandscape}) {
    height: max-content;
    width: 250px;
    border: 1px solid #d8d8d8;
    border-radius: 6px;
    background-color: transparent;
    margin-top: 50px;
    margin-right: 30px;
    padding: 30px 25px;
  }
`

const ContractOptionsTitle = styled.h1`
  color: ${colors.regular};
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.medium};
  letter-spacing: 0;
  line-height: 27px;
`

const ContractOptionsLink = styled.a`
  background: none;
  border: none;
  padding: 0;
  text-align: left;
  color: ${colors.link};
  font-size: ${fontSizes.regular};
  font-weight: ${fontWeights.medium};
  letter-spacing: 0.33px;
  line-height: 22px;
  margin-top: 20px;
  cursor: pointer;
  :hover {
    text-decoration: underline;
  }

  @media screen and (max-width: ${breakpoints.tabletLandscape}) {
    font-size: ${fontSizes.small};
    font-weight: ${fontWeights.medium};
    letter-spacing: 0.27px;
    line-height: 18px;
  }

  ${props =>
    props.disabled &&
    `
  color: ${colors.grey};
  pointer-events: none;
  cursor: default;
  `}
`

const endTenancyButtonStyle = css`
  color: ${colors.link};
  font-size: ${fontSizes.regular};
  letter-spacing: 0.33px;
  line-height: 22px;
  margin-top: 20px;
  cursor: pointer;
  :hover {
    text-decoration: underline;
  }

  @media screen and (max-width: ${breakpoints.tabletLandscape}) {
    font-size: ${fontSizes.small};
    letter-spacing: 0.27px;
    line-height: 18px;
  }
`

const ContentHeader = styled.div`
  width: 100%;
  background-color: ${colors.white};
  ${props =>
    props.sticky &&
    `
    padding-top: ${structure.header.height}px;

    @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
      padding-top: ${structure.header.heightMobile}px;
    }
  `}
  @media screen and (max-width: ${breakpoints.tabletLandscape}) {
    background-color: ${colors.lightGrey};
  }
`

const InnerContentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1096px;
  margin: 20px 40px;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    margin: 20px;
  }
`

const ContractOptionsButton = styled.button`
  display: flex;
  align-items: center;
  font-size: ${fontSizes.small};
  color: ${colors.secondary};
  font-weight: ${fontWeights.medium};
  letter-spacing: 0.27px;
  line-height: 18px;
  background: transparent;
  border: none;
  width: fit-content;
  padding: 0;
  cursor: pointer;
`

const StyledPlusIcon = styled(PlusIcon)`
  height: 25px;
  width: 25px;
`

const StyledCloseIcon = styled(CloseIcon)`
  height: 12px;
  width: 12px;
`

const DropdownWrapper = styled.div`
  display: flex;
  flex-direction: column;
  ${({ opened }) => (opened ? 'width: 100%' : '')};
`

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;

  button {
    color: ${colors.secondary};
    svg {
      g {
        stroke: ${colors.secondary};
      }
    }
  }
`

const Complete = ({ contract, user, sticky, noTitle, BackButtonComponent, history }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const role = useRole()
  const isLandlord = role === 'landlord'
  const displayMobile = useMediaQuery(`(max-width: ${breakpoints.tabletLandscape})`)
  const ref = useRef()
  const [opened, setOpened] = useState(false)

  const listingId = contract?.listingId
  const { renewalContract, nextContract } = useGetRenewalContract(listingId)

  const contractStateMachine = contract.state_machine
  const contractType = contract.contract_type

  const endTenancyRequestData = useSelector(state =>
    select.endTenancy.endTenancyRequestData(state, { listingId })
  )

  const endTenancyDone =
    endTenancyRequestData?.state_machine === 'done' &&
    endTenancyRequestData?.end_response === 'accepted'

  const toggle = () => {
    setOpened(!opened)
  }

  // This button is tricky since sometimes it calls BE,
  // and other times it links to a next contract.
  // First we check if we can call BE, and then if it has a next contact
  // to see if it should be disabled
  const canRenewOnBe = contractStateMachine === 'active' && !endTenancyDone
  const hasNextState = ['draft', 'sent'].indexOf(nextContract?.state_machine || '') > 0
  const disabled = !canRenewOnBe && !hasNextState

  const renewalStatus = () => {
    if (nextContract && nextContract.contract_type === 'extension') {
      const state = nextContract.state_machine
      if (state === 'draft') return t('l.rental_dashboard.edit.draft.label', 'Draft')
      if (state === 'sent')
        return t('l.rental_dashboard.edit.renewal_in_progress.label', 'Renewal In Progress')
    }
    if (!canRenewOnBe) return t('l.rental_dashboard.edit.not_available.label', 'Not Available')
    else if (contractStateMachine === 'active' && contractType === 'extension')
      return t('l.rental_dashboard.edit.already_renewed.label', 'Already Renewed')
    else return ''
  }

  const ContractRenewalLink = () => {
    const status = renewalStatus()
    return (
      <ContractOptionsLink
        as="button"
        type="button"
        disabled={disabled}
        onClick={async () => {
          if (nextContract && nextContract.state_machine === 'draft') {
            await history.push(`/listing/${listingId}/contract/create/${nextContract.id}`)
          } else if (nextContract && nextContract.state_machine === 'sent') {
            await history.push(`/listing/${listingId}/contract/${nextContract.id}/sign`)
          } else {
            const { body, error } = await dispatch.contract.renewContract({
              contractId: renewalContract.id,
              ...renewalContract
            })
            if (!error) {
              history.push({
                pathname: `/listing/${listingId}/contract/create/${body.id}`
              })
            }
          }
        }}
      >
        {t('l.rental_dashboard.edit.resources_renew_contract.text', 'Renew contract')}{' '}
        {status && ` - ${status}`}
      </ContractOptionsLink>
    )
  }

  const ContractOptions = () => {
    const getPdfUrl = contractId =>
      `${config.BACKEND_SERVER}/dynamic_contracts/view/contract/${contractId}/contract.pdf`
    return (
      <ContractOptionsWrapper>
        {!displayMobile && (
          <ContractOptionsTitle>
            {t('b.contract.view.contract_options.title', 'Contract Options')}
          </ContractOptionsTitle>
        )}
        <ContractOptionsLink as="a" href={getPdfUrl(contract.id)} target="_blank">
          {t('l.rental_dashboard.edit.resources_export_pdf.text', 'Save as PDF')}
        </ContractOptionsLink>
        {isLandlord && <ContractRenewalLink />}
        <ContractOptionsLink
          onClick={() =>
            showContractHistoryModal({
              listingId: contract.listing.id,
              history
            })
          }
        >
          {t('l.rental_dashboard.edit.resources_contract_history.text', 'Contract history')}
        </ContractOptionsLink>
        <EndTenancyButton
          listingId={contract.listingId}
          tenants={contract.signingTenants}
          stateMachine={contract.state_machine}
          endTenancyRequestData={endTenancyRequestData}
          customStyle={endTenancyButtonStyle}
        />
      </ContractOptionsWrapper>
    )
  }

  const ContractOption = () => {
    return (
      <>
        {displayMobile ? (
          <DropdownWrapper opened={opened}>
            <ButtonWrapper onClick={toggle}>
              <ContractOptionsButton opened={opened}>
                {t('b.contract.view.contract_options.title', 'Contract Options')}
                {!opened && <StyledPlusIcon />}
              </ContractOptionsButton>
              {opened && <StyledCloseIcon />}
            </ButtonWrapper>
            {opened && (
              <ContractOptions contract={contract} displayMobile={displayMobile}></ContractOptions>
            )}
          </DropdownWrapper>
        ) : (
          <ContractOptions contract={contract} displayMobile={displayMobile} />
        )}
      </>
    )
  }

  if (!contract.id) return null

  return (
    <>
      <ContentHeader sticky={sticky} ref={ref}>
        <InnerContentHeader>
          {BackButtonComponent && !opened && <BackButtonComponent />}
          {displayMobile && <ContractOption contract={contract} displayMobile={displayMobile} />}
        </InnerContentHeader>
      </ContentHeader>
      <WhitePage key="contract-view" bottom={120}>
        <Wrapper>
          <StyledContentWrapper>
            <Helmet>
              <title>{t('b.contract.view.contract_detail.title', 'Contract')}</title>
            </Helmet>
            {!noTitle && (
              <PageTitle>{t('b.contract.view.contract_detail.title', 'Contract')}</PageTitle>
            )}
            <Content top={0} right={0} left={0}>
              <ReviewDetails contract={contract} userType={role} isComplete />
              <ReviewSignatures contract={contract} userType={role} currentUser={user.id} />
            </Content>
          </StyledContentWrapper>
          {!displayMobile && <ContractOption contract={contract} displayMobile={displayMobile} />}
        </Wrapper>
      </WhitePage>
    </>
  )
}

Complete.defaultProps = {
  contract: {}
}

export default withRouter(Complete)
